/**
 * @file ClientMenu.tsx
 * @description Ce composant gère le menu de navigation pour le client, incluant la gestion de l'état
 * du menu (replié/déroulé) selon la taille de l'écran, et l'affichage du nom du cabinet.
 *
 * @module ClientMenu
 * @component
 *
 * @returns {JSX.Element} - Un menu de navigation avec des icônes et des liens vers différentes pages.
 *
 * @example
 * ```tsx
 * import React from 'react';
 * import ClientMenu from './ClientMenu';
 *
 * const App = () => (
 *  <div>
 *    <ClientMenu />
 *  </div>
 * );
 *
 * export default App;
 * ```
 */

import { Button, Grid, Menu, MenuProps } from 'antd';
import './ClientMenu.css';
import { HomeOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth/useAuthStore';

const { useBreakpoint } = Grid;

/**
 * @function ClientMenu
 * @description Ce composant représente le menu de navigation du client, avec un état pour
 * replier ou dérouler le menu selon la taille de l'écran.
 *
 * @returns {JSX.Element} - Le menu de navigation interactif.
 */
const ClientMenu: React.FC = () => {
  const screens = useBreakpoint(); // Gère la détection de la taille de l'écran
  const [collapsed, setCollapsed] = useState(false); // État du menu (replié/déroulé)
  const navigate = useNavigate(); // Hook pour naviguer entre les routes
  const user = useAuthStore((state) => state.user);
  const cabinetName = user ? `${user.firstName} ${user.lastName}` : ''; // Concatène le prénom et le nom de l'utilisateur

  /**
   * @useEffect Gère l'état du menu en fonction de la taille de l'écran.
   * Si l'écran est de petite ou moyenne taille (xs, sm, md), le menu sera replié.
   *
   * @param {Grid.BreakpointMap} screens - Les points de rupture de la grille Ant Design pour la réactivité.
   */
  useEffect(() => {
    if ((screens.xs || screens.sm || screens.md) && !(screens.lg || screens.xl || screens.xxl)) {
      setCollapsed(true); // Replie le menu si l'écran est de petite taille
    }
  }, [screens]);

  /**
   * @function toggleCollapsed
   * @description Inverse l'état du menu (replié/déroulé).
   */
  const toggleCollapsed = () => {
    setCollapsed(!collapsed); // Change l'état du menu (true <-> false)
  };

  /**
   * @constant {MenuProps["items"]} items - Les éléments du menu, incluant les icônes et les actions associées.
   * Chaque élément a une clé unique et un label qui représente le nom du cabinet.
   */
  const items: MenuProps['items'] = [
    {
      key: '1',
      icon: (
        <span className="icon">
          <HomeOutlined />
        </span>
      ),
      label: cabinetName, // Affiche le nom du cabinet de l'utilisateur
      onClick: () => handleItemClick('/cabinet/tableau-de-bord'), // Navigue vers le tableau de bord du cabinet
    },
  ];

  /**
   * @function handleItemClick
   * @description Redirige l'utilisateur vers une page spécifique lorsque
   * un élément du menu est cliqué.
   *
   * @param {string} [path] - Le chemin vers lequel rediriger l'utilisateur.
   */
  const handleItemClick = (path?: string) => {
    if (path) navigate(path); // Redirection vers le chemin donné
  };

  return (
    <div className="main-menu-container">
      {/* Bouton pour replier/dérouler le menu */}
      <Button type="primary" onClick={toggleCollapsed} className="menu-toggle-button">
        {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
      </Button>

      {/* Affichage du menu avec les éléments */}
      <Menu
        defaultSelectedKeys={['1']}
        defaultOpenKeys={['sub1']}
        mode="inline"
        inlineCollapsed={collapsed} // Définit l'état du menu (replié/déroulé)
        items={items} // Éléments du menu
        className="menu-content"
      />
    </div>
  );
};

export default ClientMenu;
